import { Search } from 'lucide-react';

import { Button, EmptyState } from '@/shared';
import { t } from '@/i18n';

import { getEmptyFilterAxes, type FilterAxis } from '../model/utils';
import type { CalendarFilter } from '../model/types';

// 축 이름은 필터 UI 가 이미 쓰는 라벨 키를 그대로 재사용한다 — 화면에 보이는 칩 그룹 이름과
// 빈 상태가 지목하는 이름이 갈리지 않게(같은 문구에 키를 두 개 만들지 않는다).
const AXIS_LABEL_KEY = {
  categories: 'financial_calendar.filter_drawer.category',
  countries: 'financial_calendar.filter_drawer.country',
  impacts: 'financial_calendar.impact.label',
} as const satisfies Record<FilterAxis, string>;

/**
 * TRAD-2831 — 빈 상태 문구(순수). 정확히 1개 축이 비었으면 그 축을 지목하고, 2개 이상이면
 * 일반 문구를 쓴다(android `financial_calendar_filter_empty_axis_fmt` / `_title` 과 같은 갈래·같은 문장).
 * 종전 "선택된 필터가 없습니다"는 다른 축 칩이 선택돼 보이는데 «없다»고 말해 어느 축이 문제인지 몰랐다.
 */
export const getFilterAxisEmptyTitle = (emptyAxes: readonly FilterAxis[]): string =>
  emptyAxes.length === 1
    ? t('financial_calendar.empty.filter_axis_empty', { axis: t(AXIS_LABEL_KEY[emptyAxes[0]]) })
    : t('financial_calendar.empty.no_filter_selected');

/**
 * 필터 축(카테고리·국가·중요도) 중 비어 있는 것이 있을 때의 빈 상태 — 리스트·주간·월간 뷰 공용.
 * (TRAD-2106 의 page 로컬 `CalendarEmptyDeselected` 를 옮겨 온 것 — EventList 는 같은 상태를
 * "필터 조건에 맞는 이벤트가 없습니다"로 말해 뷰마다 문구가 갈렸다.)
 * 🔴 `action` 은 "필터 초기화"뿐이다 — 재시도 버튼이 아니다(조회를 안 한 상태다).
 */
export const CalendarFilterAxisEmpty = ({
  filter,
  onReset,
}: {
  filter: Pick<CalendarFilter, FilterAxis>;
  onReset: () => void;
}) => (
  <EmptyState
    size="md"
    icon={Search}
    title={getFilterAxisEmptyTitle(getEmptyFilterAxes(filter))}
    action={
      <Button variant="outline" size="sm" onClick={onReset}>
        {t('financial_calendar.empty.reset_filter')}
      </Button>
    }
  />
);
